import { Component } from "@angular/core";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatInputModule } from "@angular/material/input";
import {
  GoldPricePayload,
  PriceService,
  SilverPricePayload,
} from "../shared/services/price.service";
import { catchError, finalize, forkJoin, of } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponentComponent } from "../dialog/confirmation-dialog-component/confirmation-dialog-component.component";
import { ToastrService } from "ngx-toastr";
import { NgxSpinnerService } from "ngx-spinner";

@Component({
  selector: "app-update-price",
  standalone: true,
  imports: [
    MatCardModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    CommonModule,
    MatInputModule,
  ],
  templateUrl: "./update-price.component.html",
  styleUrl: "./update-price.component.scss",
})
export class UpdatePriceComponent {
  priceForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private priceSvc: PriceService,
    private matDialog: MatDialog,
    private notification: ToastrService,
    private spinnerSvc: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.priceForm = this.fb.group({
      goldPrice: ["", [Validators.required, Validators.min(0)]],
      silverPrice: ["", [Validators.required, Validators.min(0)]],
    });
    this.getLatestGoldPrice();
  }

  getLatestGoldPrice() {
    this.spinnerSvc.show();

    forkJoin({
      gold: this.priceSvc.getGoldPrices().pipe(
        catchError((error) => {
          this.notification.error("Failed to fetch gold price", error?.message);
          return of({ pricePerGram: null }); // fallback value
        })
      ),
      silver: this.priceSvc.getSilverPrices().pipe(
        catchError((error) => {
          this.notification.error("Failed to fetch silver price", error?.message);
          return of({ pricePerGram: null }); // fallback value
        })
      ),
    })
      .pipe(
        finalize(() => this.spinnerSvc.hide()) // hide spinner no matter success or error
      )
      .subscribe({
        next: (results) => {
          if (results.gold?.pricePerGram) {
            this.priceForm.patchValue({ goldPrice: results.gold.pricePerGram });
          }
          if (results.silver?.pricePerGram) {
            this.priceForm.patchValue({
              silverPrice: results.silver.pricePerGram,
            });
          }
        },
        error: (err) => {
          this.notification.error("Unexpected error:", err?.message);
        },
      });
  }

  updatePrices() {
    if (this.priceForm.valid) {
      const goldPricePayload: GoldPricePayload = {
        karat: 22,
        pricePerGram: parseFloat(this.priceForm.value.goldPrice),
      };

      const silverPricePayload: SilverPricePayload = {
        pricePerGram: parseFloat(this.priceForm.value.silverPrice),
      };

      const dialogRef = this.matDialog.open(
        ConfirmationDialogComponentComponent,
        {
          width: "400px",
          data: {
            gold: goldPricePayload.pricePerGram,
            silver: silverPricePayload.pricePerGram,
          },
        }
      );

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.spinnerSvc.show();
          forkJoin({
            gold: this.priceSvc.updateGoldPrice(goldPricePayload),
            silver: this.priceSvc.updateSilverPrice(silverPricePayload),
          }).subscribe({
            next: () => {
              this.notification.success(
                "Prices updated successfully",
                "Success"
              );
              this.spinnerSvc.hide();
              this.getLatestGoldPrice();
            },
            error: (err) => {
              this.spinnerSvc.hide();
              this.notification.error("Failed to update prices", "Error");
            },
          });
        }
      });
    }
  }
}
